/**
 * System Admin — Company-Test Assignment management (11-iframe-embed/01 § Story 95).
 *
 * Lets a System Admin view and manage Test assignments for any Company.
 * Data wiring:
 *   - Company list: GET /api/admin/companies (adminApi.listCompanies)
 *   - Assignments per company: GET /api/admin/companies/:id/assignments
 *     (adminApi.listCompanyAssignments)
 *
 * The bulk assign/unassign mutation (POST /api/admin/companies/:id/assignments)
 * is available via the Company Detail page (/admin/companies/:id) which has a
 * dedicated AssignmentPicker. This page focuses on the read-only overview
 * with a link to the detail page for mutations.
 *
 * NEEDS BACKEND: A global /api/admin/assignments endpoint listing all
 * company-test assignments across all companies would be cleaner than the
 * current per-company fetch. This page works within existing endpoints.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { adminApi, type CompanyAssignmentItem } from '../../../state/admin/api.ts';
import { AdminLayout } from '../layout.tsx';
import { Badge, Card, Heading, Link } from '@/components/ui';

const EM_DASH = '—';

interface CompanySummary {
  id: string;
  name: string;
}

export function AdminAssignmentsPage(): ReactElement {
  const [companies, setCompanies] = useState<CompanySummary[] | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [assignments, setAssignments] = useState<CompanyAssignmentItem[] | null>(null);
  const [assignedCount, setAssignedCount] = useState<number>(0);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(true);
  const [loadingAssignments, setLoadingAssignments] = useState<boolean>(false);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null);

  // Load company list on mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoadingCompanies(true);
      setCompaniesError(null);
      const env = await adminApi.listCompanies({ limit: 100 });
      if (cancelled) return;
      if (!env.ok) {
        setCompaniesError(`${env.error.code}: ${env.error.message}`);
        setCompanies([]);
      } else {
        setCompanies(env.data.items.map((c) => ({ id: c.id, name: c.name })));
        // Auto-select the first company if there's only one.
        if (env.data.items.length === 1 && env.data.items[0]) {
          setSelectedCompanyId(env.data.items[0].id);
        }
      }
      setLoadingCompanies(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load assignments when a company is selected.
  useEffect(() => {
    if (!selectedCompanyId) {
      setAssignments(null);
      return;
    }
    let cancelled = false;
    setLoadingAssignments(true);
    setAssignmentsError(null);
    setAssignments(null);
    void (async () => {
      const env = await adminApi.listCompanyAssignments(selectedCompanyId, {
        include_status: 'active',
      });
      if (cancelled) return;
      if (!env.ok) {
        setAssignmentsError(`${env.error.code}: ${env.error.message}`);
        setAssignments([]);
      } else {
        // Only show tests that are assigned to this company.
        const assigned = env.data.items.filter((t) => t.assigned);
        setAssignments(assigned);
        setAssignedCount(env.data.assigned_count);
      }
      setLoadingAssignments(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId]);

  const selectedCompany = companies?.find((c) => c.id === selectedCompanyId) ?? null;

  return (
    <AdminLayout title="Manage Test Assignments" eyebrow="Admin · Assignments">
      <header className="mb-6">
        <p className="mt-2 max-w-prose font-sans text-body text-muted">
          View Test assignments per Company. To assign or revoke Tests, use the
          Company detail page.
        </p>
      </header>

      {companiesError ? (
        <p role="alert" className="mb-4 font-sans text-small text-error">
          {companiesError}
        </p>
      ) : null}

      {/* Company selector */}
      <Card variant="default" className="mb-6 p-4">
        <label
          htmlFor="company-select"
          className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
        >
          Select Company
        </label>
        {loadingCompanies ? (
          <p className="font-mono text-mono text-muted">{EM_DASH}</p>
        ) : (
          <div className="flex items-center gap-3">
            <select
              id="company-select"
              value={selectedCompanyId}
              onChange={(e) => {
                setSelectedCompanyId(e.target.value);
              }}
              className="rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
            >
              <option value="">— Select a company —</option>
              {(companies ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {selectedCompany ? (
              <Link
                to={`/admin/companies/${selectedCompanyId}`}
                className="font-sans text-small text-muted no-underline hover:text-accent"
              >
                Open Company detail →
              </Link>
            ) : null}
          </div>
        )}
      </Card>

      {/* Assignments list */}
      {selectedCompanyId ? (
        <section aria-labelledby="assigned-list">
          <div className="mb-4 flex items-baseline gap-3">
            <Heading
              id="assigned-list"
              level="h2"
              className="text-title font-medium text-ink"
            >
              Currently assigned
              {!loadingAssignments && assignments !== null
                ? ` (${String(assignedCount)})`
                : ''}
            </Heading>
          </div>

          {assignmentsError ? (
            <p role="alert" className="font-sans text-small text-error">
              {assignmentsError}
            </p>
          ) : loadingAssignments || assignments === null ? (
            <p className="font-mono text-mono text-muted" aria-live="polite">
              {EM_DASH}
            </p>
          ) : assignments.length === 0 ? (
            <p className="font-sans text-small text-muted">
              No Tests assigned to this Company yet.{' '}
              <Link to={`/admin/companies/${selectedCompanyId}`} className="hover:text-accent">
                Open Company detail
              </Link>{' '}
              to assign Tests.
            </p>
          ) : (
            <Card variant="default" className="overflow-hidden p-0">
              <table className="w-full border-collapse text-small">
                <thead>
                  <tr className="border-b border-line">
                    <th
                      scope="col"
                      className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                    >
                      Test
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                    >
                      Track / Level
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                    >
                      Assigned
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((t) => (
                    <tr
                      key={t.id}
                      className="border-b border-line last:border-b-0 hover:bg-bg-warm"
                    >
                      <td className="px-4 py-3 font-sans text-body text-ink">
                        {t.title}
                      </td>
                      <td className="px-4 py-3 font-mono text-mono text-ink-2">
                        {[t.track, t.level].filter(Boolean).join(' · ') || EM_DASH}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={t.status === 'active' ? 'success' : 'neutral'}>
                          {t.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-mono text-mono text-muted">
                        {t.assigned_at
                          ? new Date(t.assigned_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : EM_DASH}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </section>
      ) : (
        <p className="font-sans text-small text-muted">
          Select a Company above to view its assigned Tests.
        </p>
      )}

      {/* Revoking note */}
      {selectedCompanyId && assignments !== null && assignments.length > 0 ? (
        <p className="mt-4 font-sans text-small text-muted">
          To assign or revoke Tests, use the{' '}
          <Link
            to={`/admin/companies/${selectedCompanyId}`}
            className="hover:text-accent"
          >
            Company detail page
          </Link>
          . Revoking an assignment removes the catalogue grant but does NOT
          cascade to existing Attempts or already-issued Share Tokens.
        </p>
      ) : null}

    </AdminLayout>
  );
}
